import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, type User, sendEmailVerification, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, Timestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { auth, db } from './firebase';
import { UserProfile, UserRole } from './types';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Planner from './pages/Planner';
import StudentDetail from './pages/StudentDetail';
import Profile from './pages/Profile';
import Stats from './pages/Stats';
import Reviews from './pages/Reviews';
import Support from './pages/Support';
import Guide from './pages/Guide';
import Programs from './pages/Programs';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Pricing from './pages/Pricing';
import Marketplace from './pages/Marketplace';
import PublicProfileView from './pages/PublicProfileView';
import EditPublicProfile from './pages/EditPublicProfile';
import Layout from './components/Layout';
// Add Link to router imports
import { HashRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { Mail, ShieldAlert, LogOut, RefreshCw } from 'lucide-react';
import { TranslationProvider, useTranslation } from './lib/translations';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const MainApp: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkingInvitations, setCheckingInvitations] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const { t } = useTranslation();

  const checkForInvitations = async (uid: string, email: string): Promise<UserRole> => {
    const userEmail = email.toLowerCase().trim();
    const allowlistQuery = query(collection(db, 'allowlist'), where('email', '==', userEmail));
    const allowlistSnap = await getDocs(allowlistQuery);
    if (!allowlistSnap.empty) return 'teacher';

    const relQuery = query(collection(db, 'relationships'), where('studentId', '==', uid));
    const relSnap = await getDocs(relQuery);
    
    const pendingId = 'pending:' + userEmail;
    const pendingRelQuery = query(collection(db, 'relationships'), where('studentId', '==', pendingId));
    const pendingRelSnap = await getDocs(pendingRelQuery);
    
    if (!relSnap.empty || !pendingRelSnap.empty) {
      if (!pendingRelSnap.empty) {
        const batch = writeBatch(db);
        pendingRelSnap.docs.forEach(relDoc => {
          batch.update(relDoc.ref, { studentId: uid });
        });
        const lessonsQuery = query(collection(db, 'lessons'), where('studentId', '==', pendingId));
        const lessonsSnap = await getDocs(lessonsQuery);
        lessonsSnap.docs.forEach(lessonDoc => {
          batch.update(lessonDoc.ref, { studentId: uid });
        });
        await batch.commit();
      }
      return 'student';
    }
    return null;
  };

  const handleCheckAccess = async () => {
    if (!user || !profile) return;
    setCheckingInvitations(true);
    const newRole = await checkForInvitations(user.uid, user.email || '');
    if (newRole) {
      const updates: Partial<UserProfile> = { role: newRole };
      if (newRole === 'teacher') updates.currency = 'EUR';
      await updateDoc(doc(db, 'users', user.uid), updates);
      setProfile({ ...profile, ...updates } as UserProfile);
    }
    setCheckingInvitations(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userDocRef);
        const userEmail = firebaseUser.email?.toLowerCase().trim() || '';

        if (userDoc.exists()) {
          const data = userDoc.data() as UserProfile;
          if (data.role === null) {
            const resolvedRole = await checkForInvitations(firebaseUser.uid, userEmail);
            if (resolvedRole) {
              const updates: Partial<UserProfile> = { role: resolvedRole };
              if (resolvedRole === 'teacher') updates.currency = 'EUR';
              await updateDoc(userDocRef, updates);
              data.role = resolvedRole;
            }
          }
          // Ensure new fields exist for legacy users
          if (data.firstName === undefined || data.lastName === undefined) {
             const parts = (data.displayName || '').split(' ');
             data.firstName = parts[0] || '';
             data.lastName = parts.slice(1).join(' ') || '';
             await updateDoc(userDocRef, { firstName: data.firstName, lastName: data.lastName });
          }
          setProfile(data);
        } else {
          const role = await checkForInvitations(firebaseUser.uid, userEmail);
          const fullDisplayName = firebaseUser.displayName || userEmail.split('@')[0] || 'Anonymous User';
          const parts = fullDisplayName.split(' ');
          const firstName = parts[0] || '';
          const lastName = parts.slice(1).join(' ') || '';

          const newProfile: UserProfile = {
            uid: firebaseUser.uid,
            email: userEmail,
            displayName: fullDisplayName,
            firstName,
            lastName,
            photoURL: firebaseUser.photoURL || '',
            role,
            createdAt: Timestamp.now(),
          };
          if (role === 'teacher') newProfile.currency = 'EUR';
          await setDoc(userDocRef, newProfile);
          setProfile(newProfile);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleResendVerification = async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
      setVerificationSent(true);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('google_access_token');
    signOut(auth);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <HashRouter>
      <ScrollToTop />
      {!user ? (
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/teachers" element={<Marketplace profile={null} />} />
          <Route path="/teacher/:id" element={<PublicProfileView user={null} profile={null} />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<Support profile={null} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      ) : !user.emailVerified && user.providerData[0]?.providerId === 'password' ? (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center shadow-2xl">
            <div className="inline-flex p-4 bg-purple-600/10 text-purple-600 rounded-xl mb-6">
              <Mail size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-4">{t.app.verifyEmail}</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">
              {t.app.linkSent} <span className="text-slate-900 dark:text-slate-100 font-semibold">{user.email}</span>.
            </p>
            <div className="space-y-4">
              <button onClick={handleResendVerification} disabled={verificationSent} className="w-full bg-purple-600 text-white py-4 rounded-lg font-bold">
                {verificationSent ? t.app.linkResent : t.app.resendLink}
              </button>
              <button onClick={() => window.location.reload()} className="w-full bg-slate-100 dark:bg-slate-800 py-4 rounded-lg font-bold text-slate-900 dark:text-white">
                {t.app.verifiedAction}
              </button>
              <button onClick={handleLogout} className="w-full text-slate-500 hover:text-red-500 font-medium py-2 flex items-center justify-center gap-2">
                <LogOut size={18} /> {t.nav.signOut}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <Layout profile={profile}>
          <Routes>
            {/* Common Routes */}
            <Route path="/teachers" element={<Marketplace profile={profile} />} />
            <Route path="/teacher/:id" element={<PublicProfileView user={user} profile={profile} />} />
            <Route path="/profile" element={<Profile profile={profile} onUpdate={setProfile} />} />
            <Route path="/support" element={<Support profile={profile} />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/pricing" element={<Pricing />} />

            {/* Guest / No Role yet */}
            {profile?.role === null && (
              <>
                <Route path="/" element={
                  <div className="flex flex-col items-center justify-center py-20 text-center space-y-6">
                    <ShieldAlert size={64} className="text-amber-500" />
                    <h2 className="text-3xl font-black">{t.app.accessPending}</h2>
                    <p className="text-slate-500 max-w-md">{t.app.accessPendingDesc}</p>
                    <div className="flex gap-4">
                      <button onClick={handleCheckAccess} className="bg-purple-600 text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2">
                        {checkingInvitations ? <RefreshCw className="animate-spin" size={18} /> : <RefreshCw size={18} />} {t.app.checkAccess}
                      </button>
                      {/* Fix: replace undefined navigate with Link component */}
                      <Link to="/teachers" className="bg-slate-100 dark:bg-slate-800 px-8 py-3 rounded-xl font-bold">
                        {t.marketplace.title}
                      </Link>
                    </div>
                  </div>
                } />
                <Route path="*" element={<Navigate to="/" />} />
              </>
            )}

            {/* Roles Specific Routes */}
            {profile?.role === 'teacher' && (
              <>
                <Route path="/" element={<Dashboard profile={profile} />} />
                <Route path="/students" element={<Students profile={profile} />} />
                <Route path="/planner" element={<Planner profile={profile} />} />
                <Route path="/stats" element={<Stats profile={profile} />} />
                <Route path="/reviews" element={<Reviews profile={profile} />} />
                <Route path="/programs" element={<Programs profile={profile} />} />
                <Route path="/public-profile" element={<EditPublicProfile profile={profile} />} />
                <Route path="/student/:id" element={<StudentDetail profile={profile} />} />
                <Route path="/guide" element={<Guide />} />
              </>
            )}

            {profile?.role === 'student' && (
              <>
                <Route path="/" element={<Dashboard profile={profile} />} />
                <Route path="/student/:id" element={<StudentDetail profile={profile} />} />
              </>
            )}
            
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Layout>
      )}
    </HashRouter>
  );
};

const App: React.FC = () => {
  return (
    <TranslationProvider>
      <MainApp />
    </TranslationProvider>
  );
};

export default App;